import { createStyles, Drawer, makeStyles, Theme } from '@material-ui/core'

import ShowToRoleAndStates from 'components/Common/Portal/ShowToRole'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useAppSelector } from 'store/configureStore'
import { RootAppState } from 'store/configureStore'
import { SettingsItem } from './SettingsItem'
import { SettingsSection } from './SettingsSection'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      overflowX: 'hidden',
      width: '264px',
      height: 'calc(100vh - 64px)',
      flexShrink: 0,
      '& .MuiDrawer-paper': {
        position: 'inherit',
      },
    },
    title: {
      textAlign: 'center',
      width: '100%',
      padding: theme.spacing(2),
    },
  })
)

export const SettingsMenu = () => {
  const cn = useStyles()
  const { t } = useTranslation('settings')
  const location = useLocation()

  const isBuyingParty = useAppSelector(
    (state: RootAppState) => state.user.organizationContext.isBuyingParty
  )

  return (
    <Drawer open={true} variant="persistent" className={cn.root}>
      <LocalizedTypography
        translationKey="settings:menu-title"
        variant="h6"
        className={cn.title}
        display="block"
      >
        organization settings
      </LocalizedTypography>
      <ul>
        <SettingsSection
          title={t('settings:menu-general')}
          isOpen={location.pathname.includes('/settings/general/')}
          level={1}
        >
          <SettingsItem
            title={t(
              'settings:menu-organization-profile',
              'organization profile'
            )}
            level={2}
            to={'/app/settings/general/organization'}
          />
          <SettingsItem
            title={t('settings:menu-manage-users')}
            level={2}
            to={'/app/settings/general/user-management'}
          />
          <SettingsItem
            title={t('settings:menu-user-profile', 'user profile')}
            level={2}
            to={'/app/settings/general/user'}
          />
          <ShowToRoleAndStates isBuyerRole={isBuyingParty} seller>
            <>
              <SettingsItem
                level={2}
                title={t('settings:machining-settings', 'machining settings')}
                to={'/app/settings/general/machining-settings'}
              />

              <SettingsSection
                level={2}
                title={t('settings:menu-nesting')}
                isOpen={location.pathname.includes('settings/general/nesting')}
              >
                <SettingsItem
                  level={3}
                  title={t(
                    'settings:menu-nesting-default-settings',
                    'default settings'
                  )}
                  to={'/app/settings/general/nesting/default'}
                />
              </SettingsSection>
              <SettingsSection
                title={t('settings:menu-drawing-settings', 'drawing settings')}
                level={2}
                isOpen={location.pathname.includes(
                  '/settings/general/drawings'
                )}
              >
                <SettingsItem
                  level={3}
                  title={t('settings:dxf-settings', 'dxf settings')}
                  to={'/app/settings/general/drawings/dxf'}
                />
                <SettingsItem
                  level={3}
                  title={t('settings:cad-file-patterns', 'cad file patterns')}
                  to={'/app/settings/general/drawings/cad-file-patterns'}
                />
              </SettingsSection>
              <SettingsSection
                title={t('settings:menu-project-export', 'project export')}
                level={2}
                isOpen={location.pathname.includes('/settings/general/export/')}
              >
                <SettingsItem
                  title={t(
                    'settings:menu-excel-export-templates',
                    'excel templates'
                  )}
                  level={3}
                  to={'/app/settings/general/export/templates'}
                />
              </SettingsSection>
            </>
          </ShowToRoleAndStates>
        </SettingsSection>
        <ShowToRoleAndStates isBuyerRole={isBuyingParty} seller>
          <>
            <SettingsSection
              title={t('settings:menu-virtual-factory', 'virtual factory')}
              level={1}
              isOpen={location.pathname.includes('/settings/virtual-factory/')}
            >
              <SettingsSection
                level={2}
                title={t('settings:menu-articles', 'articles')}
                isOpen={location.pathname.includes(
                  '/settings/virtual-factory/articles/'
                )}
              >
                <SettingsItem
                  level={3}
                  title={t('settings:menu-articles-upload', 'upload')}
                  to={'/app/settings/virtual-factory/articles/management'}
                />
                <SettingsItem
                  level={3}
                  title={t('settings:menu-pricing', 'pricing')}
                  to={'/app/settings/virtual-factory/articles/pricing'}
                />
                <SettingsItem
                  level={3}
                  title={t('settings:menu-discounts-surcharges')}
                  to={
                    '/app/settings/virtual-factory/articles/discount-surcharge'
                  }
                />
              </SettingsSection>
              <SettingsSection
                level={2}
                title={t('settings:menu-materials')}
                isOpen={location.pathname.includes(
                  '/settings/virtual-factory/materials/'
                )}
              >
                <SettingsItem
                  title={t('settings:menu-standards')}
                  level={3}
                  to={'/app/settings/virtual-factory/materials/standards'}
                />
              </SettingsSection>
              <SettingsSection
                level={2}
                isOpen={location.pathname.includes(
                  '/settings/virtual-factory/capabilities/'
                )}
                title={t('settings:menu-capabilities')}
              >
                <SettingsItem
                  level={3}
                  title={t('settings:menu-resources', 'resources')}
                  to={'/app/settings/virtual-factory/capabilities/resources'}
                />
                <SettingsItem
                  level={3}
                  title={t(
                    'settings:menu-discounts-surcharges',
                    'discounts & surcharges'
                  )}
                  to={
                    '/app/settings/virtual-factory/capabilities/discount-surcharge'
                  }
                />
              </SettingsSection>
              <SettingsSection
                level={2}
                isOpen={location.pathname.includes(
                  '/settings/virtual-factory/project-delivery-estimation'
                )}
                title={t(
                  'settings:menu-delivery-estimation',
                  'delivery estimation'
                )}
              >
                <SettingsItem
                  level={3}
                  title={t(
                    'settings:project-delivery-estimation',
                    'delivery days estimation'
                  )}
                  to={
                    '/app/settings/virtual-factory/project-delivery-estimation'
                  }
                />
              </SettingsSection>
            </SettingsSection>

            <SettingsSection
              title={t('settings:menu-supply-chain', 'supply chain')}
              level={1}
              isOpen={location.pathname.includes('/settings/customers/')}
            >
              <SettingsSection title={t('settings:menu-customers')} level={3}>
                <SettingsItem
                  title={t('settings:menu-customers-address-book')}
                  level={4}
                  to={'/app/settings/customers/address-book'}
                />
              </SettingsSection>
            </SettingsSection>
            <SettingsSection
              title={t('settings:menu-business-rules', 'rules')}
              level={1}
              isOpen={location.pathname.includes('/settings/rules/')}
            >
              <SettingsItem
                title={t(
                  'settings:menu-business-rules-auto-quote',
                  'auto quote'
                )}
                level={3}
                to={'/app/settings/rules/auto-quote'}
              />
              <SettingsItem
                title={t(
                  'settings:menu-business-rules-auto-add-working-steps-at-part-level',
                  'auto add working step(s) at part level'
                )}
                level={3}
                to={'/app/settings/rules/auto-add-working-steps'}
              />

              <SettingsItem
                title={t(
                  'settings:menu-business-rules-auto-add-working-steps-at-project-level',
                  'auto add working step(s) at project level'
                )}
                level={3}
                to={
                  '/app/settings/rules/auto-add-working-steps-at-project-level'
                }
              />
              <SettingsItem
                title={t(
                  'settings:menu-business-rules-auto-assign-materials',
                  'auto assign materials'
                )}
                level={3}
                to={'/app/settings/rules/auto-assign-materials'}
              />
              <SettingsItem
                title={t(
                  'settings:menu-business-rules-auto-start-manufacturability-check',
                  'auto start manufacturability check'
                )}
                level={3}
                to={'/app/settings/rules/auto-start-manufacturability-check'}
              />
              <SettingsItem
                title={t(
                  'settings:menu-business-rules-remnants',
                  'remnant strategies'
                )}
                level={3}
                to={'/app/settings/rules/remnants'}
              />
              <SettingsItem
                title={t('settings:menu-business-rules-nest', 'nest')}
                level={3}
                to={'/app/settings/rules/nest'}
              />
            </SettingsSection>
            <SettingsSection
              title={t('settings:menu-customer-portal', 'customer portal')}
              level={1}
              isOpen={location.pathname.includes('settings/portal/')}
            >
              <SettingsSection
                title={t('settings:menu-working-steps', 'working steps')}
                level={2}
                isOpen={location.pathname.includes('/portal/working-steps')}
              >
                <SettingsItem
                  title={t(
                    'settings:menu-available-working-steps',
                    'available working steps'
                  )}
                  level={3}
                  to={'/app/settings/portal/working-steps'}
                />
              </SettingsSection>
              <SettingsSection
                title={t('settings:menu-portal-articles', 'articles')}
                level={2}
                isOpen={location.pathname.includes('/portal/articles')}
              >
                <SettingsItem
                  title={t(
                    'settings:menu-portal-available-articles',
                    'available articles'
                  )}
                  level={3}
                  to={'/app/settings/portal/articles/available-articles'}
                />
              </SettingsSection>

              <SettingsSection
                title={t(
                  'settings: menu-portal-technical-settings',
                  'technical settings'
                )}
                level={2}
                isOpen={
                  location.pathname.includes('/portal/sdk-settings') ||
                  location.pathname.includes('/portal/email-settings')
                }
              >
                <SettingsItem
                  level={3}
                  title={t('settings:menu-integration-webapp')}
                  to={'/app/settings/portal/sdk-settings'}
                />
                <SettingsItem
                  level={3}
                  title={t('settings:menu-portal-email-settings')}
                  to={'/app/settings/portal/email-settings'}
                />
              </SettingsSection>
              <SettingsSection
                title={t('settings:menu-portal-email-templates')}
                level={2}
                isOpen={location.pathname.includes('portal/email-templates/')}
              >
                <SettingsItem
                  level={3}
                  title={t(
                    `settings:email-template-description-ProjectStatusChanged`
                  )}
                  to={`/app/settings/portal/email-templates/project-status-changed/defining`}
                />
                <SettingsItem
                  level={3}
                  title={t(`settings:email-template-description-AcceptInvite`)}
                  to={`/app/settings/portal/email-templates/accept-invite`}
                />
              </SettingsSection>
            </SettingsSection>
            <SettingsSection
              title={t('settings:menu-documents')}
              level={1}
              isOpen={location.pathname.includes('/settings/documents/')}
            >
              <SettingsItem
                level={2}
                title={t('settings:menu-templates', 'templates')}
                to={'/app/settings/documents/templates'}
              />
            </SettingsSection>
            <SettingsSection
              level={1}
              isOpen={location.pathname.includes('/settings/integrations/')}
              title={t('settings:menu-integrations')}
            >
              <SettingsItem
                level={2}
                title={t('settings:menu-webhooks')}
                to={'/app/settings/integrations/webhooks'}
              ></SettingsItem>
              <SettingsItem
                level={2}
                title={t('settings:menu-agents')}
                to={'/app/settings/integrations/agents'}
              />
              <SettingsItem
                level={2}
                title={t('settings:menu-integration-store')}
                to={'/app/settings/integrations/store'}
              />
              <SettingsItem
                level={2}
                title={t('settings:menu-integration-imnoo', 'imnoo')}
                to={'/app/settings/integrations/imnoo'}
              />
              <SettingsItem
                level={2}
                title={t('settings:menu-integration-werk24', 'werk24')}
                to={'/app/settings/integrations/werk24'}
              />
            </SettingsSection>
          </>
        </ShowToRoleAndStates>
      </ul>
    </Drawer>
  )
}
